import { Button, Group, Box, useMantineTheme } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { CategoryOptionType } from "src/generated/graphql";
import { memo } from "react";
import TextInput from "src/components/TextInput/TextInput";
import Textarea from "src/components/Textarea/Textarea";
import { ObjectSchema, AnyObject } from "yup";

export enum InputFormElement {
  INPUT = "input",
  TEXTAREA = "textArea",
}

const InputComponents = {
  [InputFormElement.INPUT]: TextInput,
  [InputFormElement.TEXTAREA]: Textarea,
};

type NameInputFormProps<T> = {
  type: CategoryOptionType | T;
  schema: ObjectSchema<
    {
      name: string;
    },
    AnyObject,
    {
      name: undefined;
    },
    ""
  >;
  value?: string;
  onCancel: () => void;
  onSubmit: ({ name }: { name: string }) => void;
  maxWidth?: number;
  inputType?: InputFormElement;
  loading?: boolean;
};

const NameInputForm = <T extends string>({
  value,
  onCancel,
  onSubmit,
  type,
  schema,
  maxWidth = 360,
  inputType = InputFormElement.INPUT,
  loading = false,
}: NameInputFormProps<T>) => {
  const theme = useMantineTheme();
  const form = useForm({
    initialValues: {
      name: value || "",
    },
    validate: yupResolver(schema),
  });
  const InputEl = InputComponents[inputType];
  return (
    <Box sx={{ maxWidth }} mx="auto">
      <form
        onSubmit={form.onSubmit((values) => {
          onSubmit(values);
        })}
      >
        <InputEl
          withAsterisk
          label={
            Object.values(CategoryOptionType).includes(
              type as CategoryOptionType
            )
              ? `${type} Name`
              : type
          }
          {...form.getInputProps("name")}
        />

        <Group position="apart" mt="md" style={{ marginTop: 30 }}>
          <Button
            radius="md"
            variant="outline"
            onClick={onCancel}
            style={{
              flex: 1,
              color: theme.colors.secondaryTextColor[6],
            }}
            sx={{
              backgroundColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.secondary[5],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            radius="md"
            type="submit"
            loading={loading}
            style={{
              flex: 1,
            }}
            sx={{
              color: theme.colors.primaryTextColor[6],
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
          >
            {value ? "Save" : "Create"}
          </Button>
        </Group>
      </form>
    </Box>
  );
};

export default memo(NameInputForm);
